import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getCart, getPacketaWidgetConfig, checkout, EshopCart } from '../services/eshopApi';
import {
  Box,
  Heading,
  Text,
  VStack,
  Button,
  FormControl,
  FormLabel,
  Input,
  Radio,
  RadioGroup,
  Stack,
  Divider,
  HStack,
  useToast,
  Card,
  CardBody,
  Container,
  Spinner,
  FormErrorMessage,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';

declare global {
  interface Window {
    Packeta: any;
  }
}

const formatPrice = (cents: number, currency: string) => {
  const value = cents / 100;
  return new Intl.NumberFormat('cs-CZ', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
  }).format(value);
};

const CheckoutPage: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { data: cart, isLoading: cartLoading } = useQuery<EshopCart>(['eshop-cart'], getCart);
  const { data: packetaConfig, isLoading: packetaLoading, isError: packetaError } = useQuery(
    ['packeta-config'],
    getPacketaWidgetConfig,
  );

  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [shippingMethod, setShippingMethod] = useState('packeta');
  const [selectedPoint, setSelectedPoint] = useState<any>(null);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showValidationErrors, setShowValidationErrors] = useState(false);

  useEffect(() => {
    // Load Packeta widget script
    const script = document.createElement('script');
    script.src = 'https://widget.packeta.com/v6/www/js/library.js';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const openPacketaWidget = () => {
    if (!window.Packeta || !packetaConfig) {
      toast({ status: 'error', title: 'Chyba', description: 'Widget Zásilkovny není připraven.' });
      return;
    }

    window.Packeta.Widget.pick(packetaConfig.api_key, (point: any) => {
      if (point) {
        setSelectedPoint(point);
      }
    }, {
      country: 'cz,sk',
      language: 'cs',
    });
  };

  const handleSubmit = async () => {
    setShowValidationErrors(true);

    // Basic validation
    if (!email || !firstName || !lastName || !phone) {
      toast({
        status: 'warning',
        title: 'Chybějící údaje',
        description: 'Vyplňte prosím všechny kontaktní údaje.',
      });
      return;
    }
    if (shippingMethod === 'packeta' && !selectedPoint) {
      toast({ status: 'warning', title: 'Doprava', description: 'Vyberte prosím výdejní místo.' });
      return;
    }

    setIsSubmitting(true);
    try {
      const billingAddress = {
        firstName, lastName, email, phone
      };
      
      const shippingAddress = shippingMethod === 'packeta' 
        ? selectedPoint 
        : billingAddress; // Fallback

      const res = await checkout({
        email,
        first_name: firstName,
        last_name: lastName,
        phone,
        billing_address: billingAddress,
        shipping_address: shippingAddress,
        shipping_method: shippingMethod,
      });

      // Manual email fallback (no online gateway)
      if (res.manual_payment && res.contact_email) {
        toast({
          status: 'info',
          title: 'Dokončení objednávky',
          description: `Online platba není aktuálně dostupná. Objednávku prosím pošlete na tento e-mail: ${res.contact_email}`,
          duration: 15000,
          isClosable: true,
        });

        // I bez online platby chceme zákazníkovi ukázat shrnutí objednávky
        if (res.order_id) {
          navigate(`/objednavka/dekujeme?order_id=${res.order_id}`);
        }
        return;
      }

      // Online payment via redirect (GoCardless / GoPay)
      if (res.payment_redirect_url) {
        window.location.href = res.payment_redirect_url;
        return;
      }

      toast({
        status: 'error',
        title: 'Platba',
        description: 'Platební brána není aktuálně dostupná. Zkuste to prosím později nebo kontaktujte podporu.',
      });
    } catch (err) {
      toast({ status: 'error', title: 'Chyba', description: 'Nepodařilo se vytvořit objednávku.' });
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (cartLoading) return <Spinner />;
  if (!cart || !cart.items || cart.items.length === 0) {
    return (
      <Container maxW="3xl" py={10}>
        <Heading size="lg" mb={4}>Košík je prázdný</Heading>
        <Button onClick={() => navigate('/')}>Zpět do obchodu</Button>
      </Container>
    );
  }

  const itemsTotal = cart.items.reduce((sum, it) => sum + it.unit_price_cents * it.quantity, 0);
  const shippingPriceCents = shippingMethod === 'packeta' ? 7900 : 0; // match backend logic
  const grandTotal = itemsTotal + shippingPriceCents;

  return (
    <Container maxW="3xl" py={8}>
      <Heading mb={6}>Pokladna</Heading>
      
      <VStack spacing={8} align="stretch">
        {/* Contact Info */}
        <Card variant="outline">
          <CardBody>
            <Heading size="md" mb={4}>1. Kontaktní údaje</Heading>
            <VStack spacing={4}>
              <FormControl isRequired isInvalid={showValidationErrors && !email}>
                <FormLabel>Email</FormLabel>
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
                <FormErrorMessage>Zadejte prosím e-mail.</FormErrorMessage>
              </FormControl>
              <HStack width="100%">
                <FormControl isRequired isInvalid={showValidationErrors && !firstName}>
                  <FormLabel>Jméno</FormLabel>
                  <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} />
                  <FormErrorMessage>Zadejte prosím jméno.</FormErrorMessage>
                </FormControl>
                <FormControl isRequired isInvalid={showValidationErrors && !lastName}>
                  <FormLabel>Příjmení</FormLabel>
                  <Input value={lastName} onChange={(e) => setLastName(e.target.value)} />
                  <FormErrorMessage>Zadejte prosím příjmení.</FormErrorMessage>
                </FormControl>
              </HStack>
              <FormControl isRequired isInvalid={showValidationErrors && !phone}>
                <FormLabel>Telefon</FormLabel>
                <Input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} />
                <FormErrorMessage>Zadejte prosím telefon.</FormErrorMessage>
              </FormControl>
            </VStack>
          </CardBody>
        </Card>

        {/* Shipping Method */}
        <Card variant="outline">
          <CardBody>
            <Heading size="md" mb={4}>2. Doprava</Heading>
            <FormControl
              isRequired
              isInvalid={showValidationErrors && shippingMethod === 'packeta' && !selectedPoint}
            >
              <RadioGroup value={shippingMethod} onChange={setShippingMethod}>
                <Stack direction="column">
                  <Radio value="packeta">Zásilkovna (výdejní místa)</Radio>
                  {/* Future: Add other carriers */}
                </Stack>
              </RadioGroup>

              {shippingMethod === 'packeta' && (
                <Box mt={4} p={4} borderWidth="1px" borderRadius="md" bg="gray.50">
                  {packetaLoading && (
                    <Text fontSize="sm" color="gray.600" mb={2}>
                      Načítání widgetu Zásilkovny...
                    </Text>
                  )}
                  {packetaError && (
                    <Text fontSize="sm" color="red.500" mb={2}>
                      Nepodařilo se načíst konfiguraci Zásilkovny. Zkuste to prosím později.
                    </Text>
                  )}

                  {selectedPoint ? (
                    <VStack align="start">
                      <Text fontWeight="bold">Vybrané místo:</Text>
                      <Text>{selectedPoint.name}</Text>
                      <Text fontSize="sm" color="gray.600">{selectedPoint.street}, {selectedPoint.city}</Text>
                      <Button
                        size="sm"
                        onClick={openPacketaWidget}
                        variant="outline"
                        mt={2}
                        isDisabled={!!packetaError}
                      >
                        Změnit místo
                      </Button>
                    </VStack>
                  ) : (
                    <Button
                      onClick={openPacketaWidget}
                      colorScheme="red"
                      isDisabled={packetaLoading || !!packetaError}
                    >
                      Vybrat výdejní místo
                    </Button>
                  )}
                </Box>
              )}

              <FormErrorMessage>Vyberte prosím výdejní místo.</FormErrorMessage>
            </FormControl>
          </CardBody>
        </Card>

        {/* Summary */}
        <Card variant="outline">
          <CardBody>
            <Heading size="md" mb={4}>3. Shrnutí objednávky</Heading>
            <VStack align="stretch" spacing={2} mb={4}>
              {cart.items.map((item) => (
                <HStack key={item.id} justify="space-between">
                  <Text>{item.product?.name || 'Produkt'} x {item.quantity}</Text>
                  <Text>
                    {formatPrice(item.unit_price_cents * item.quantity, item.currency || cart.currency)}
                  </Text>
                </HStack>
              ))}
              <Divider my={2} />
              <HStack justify="space-between" fontWeight="bold">
                <Text>Celkem zboží</Text>
                <Text>{formatPrice(itemsTotal, cart.currency)}</Text>
              </HStack>
              <HStack justify="space-between">
                <Text>Doprava</Text>
                <Text>
                  {shippingPriceCents > 0
                    ? formatPrice(shippingPriceCents, cart.currency)
                    : 'Bude upřesněno'}
                </Text>
              </HStack>
              <Divider my={2} />
              <HStack justify="space-between" fontWeight="bold">
                <Text>Celkem k úhradě</Text>
                <Text>{formatPrice(grandTotal, cart.currency)}</Text>
              </HStack>
            </VStack>

            <Button 
              colorScheme="blue" 
              size="lg" 
              width="100%" 
              onClick={handleSubmit} 
              isLoading={isSubmitting}
            >
              Pokračovat k platbě
            </Button>
          </CardBody>
        </Card>
      </VStack>
    </Container>
  );
};

export default CheckoutPage;
